<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div
        class="kds-typography-title-medium"
        v-html="$t('b2b_self_test_generic_assessment')"
        v-if="showHeaderTitle"
      />
    </template>
    <div class="kds-main-wrapper">
      <h3
        class="kds-typography-title-xlarge"
        v-html="$t('b2b_self_test_pain_intensity_question')"
      />
      <ScoreSlider
        :label="getScoreSliderLabel"
        :max-score="10"
        :max-text="$t('b2b_self_test_pain_intensity_severe_pain')"
        :min-text="$t('b2b_self_test_pain_intensity_no_pain')"
        :score="getScoreSliderValue"
        data-qa="score-slider"
        @change="handlePainIntensityChange"
        @tap="handlePainIntensityChange"
      />
    </div>
    <template #actions>
      <BaseButton
        :disabled="!isValid"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="next"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import BaseButton from '@shared/components/BaseButton.vue'
import ScoreSlider from '@shared/components/ScoreSlider.vue'
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import { isFromApp } from '@shared/helpers/isFromApp'
import usePreSubscriptionWebOptimizedFunnel from '@shared/composables/usePreSubscriptionWebOptimizedFunnel'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'
import { preloadImage } from '@shared/utils/preloadImage'
import testimonialAvatarFemale from '@shared/assets/img/testimonial_avatar_female.svg'

const selfTestStore = useSelfTestStore()
const preSubscriptionWebOptimizedFunnel = usePreSubscriptionWebOptimizedFunnel()
const { t } = useI18n()

const showHeaderTitle = computed(
  () => !preSubscriptionWebOptimizedFunnel.value && isFromApp(),
)

onMounted(() => preloadImage(testimonialAvatarFemale))

const props = defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const painIntensity = ref(
  selfTestStore.painLevelAwaitsSecondAnswer ? -1 : selfTestStore.painIntensity,
)

const getScoreSliderValue = computed(() =>
  painIntensity.value === -1 ? 5 : painIntensity.value,
)

const getScoreSliderLabel = computed(() =>
  painIntensity.value === -1 ? '' : `${painIntensity.value}`,
)

const isValid = computed(
  () => painIntensity.value >= 0 && painIntensity.value <= 10,
)

const handlePainIntensityChange = ({ value }) => {
  painIntensity.value = value
}

const next = () => {
  if (!isValid.value) return
  // The slider shows the number; the value is its own text.
  selfTestStore.answer('pain_level', {
    value: painIntensity.value,
    text: String(painIntensity.value),
    questionText: t('b2b_self_test_pain_intensity_question'),
  })
  props.handleNextEvent()
}
</script>
